import { Accordion, BackIcon, IconButton } from "@au-schein-de/au-ui-library";
import { type MouseEvent, Suspense, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
	useLoaderData,
	useLocation,
	useNavigate,
	useRouteLoaderData,
} from "react-router";
import type { loader } from "~/routes/_storefront._products.product.$slug.$productId";
import type { Route } from "../../../../.react-router/types/app/+types/root";
import { getShareUrl } from "../get-share-url";
import { ProductDetailsCTA } from "../product-details-cta.component";
import { ProductDetailsDescriptionAccordion } from "../product-details-description-accordion.component";
import { ProductDetailsHeader } from "../product-details-header.component";
import { ShareButton } from "../share-button.component";
import { useProductDetailsVendorState } from "../use-product-details-vendor-state.hook";

export function OtcProductDetails() {
	const rootLoaderData =
		useRouteLoaderData<Route.ComponentProps["loaderData"]>("root");
	const enableVariantSelector =
		rootLoaderData?.featureFlags?.enableVariantSelector ?? false;
	const { product, ratingBreakdown } = useLoaderData<typeof loader>();
	const {
		localVendorId,
		handleVendorChange,
		variants,
		effectivePreferredVendorId,
	} = useProductDetailsVendorState({ product, enableVariantSelector });
	const { name, kind } = product;

	const navigate = useNavigate();
	const location = useLocation();

	const { t } = useTranslation(["common", "product"]);

	const handleGoBack = useCallback(
		async (e: MouseEvent<unknown>) => {
			e.preventDefault();
			if (location.state?.from === "/products") {
				navigate(-1);
			} else {
				navigate(`/products${location.search}`);
			}
		},
		[navigate, location],
	);

	const productFullName = `${name}: ${kind}`;

	return (
		<div className="-mx-4 md:mx-0">
			<div className="mt-0 md:mt-4 notranslate" translate="no">
				<div className="w-full">
					<div className="flex md:hidden items-center justify-between bg-gray-100 pt-2 px-4">
						<IconButton
							type="button"
							aria-label={t("common:backToAllProducts")}
							icon={<BackIcon size={6} colorToken="gray-700" />}
							shape="circle"
							color="gray"
							className="size-11 min-h-11 min-w-11 bg-white border-0 shadow-none hover:bg-white active:bg-white"
							onClick={handleGoBack}
							data-testid="product-details-back-mobile"
						/>
						<ShareButton
							title={t("product:share.product.title", {
								productName: productFullName,
							})}
							text={t("product:share.product.text", {
								productName: productFullName,
							})}
							url={
								typeof window !== "undefined"
									? getShareUrl(window.location.href)
									: ""
							}
						/>
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full mt-2 md:mt-0 mb-0 [grid-template-areas:'header'_'cta'_'description'] md:[grid-template-areas:'header_cta'_'description_cta']">
					{/* Header (images + name) — area: header */}
					<div className="[grid-area:header]">
						<div className="grid grid-cols-1 grid-rows-[226px_auto] md:grid-rows-[340px_auto]">
							<ProductDetailsHeader
								product={product}
								badges={[]}
								backLabel={t("common:backToAllProducts")}
								onBack={handleGoBack}
							/>
						</div>
					</div>

					{/* CTA: price + "buying from" vendor + add to cart — area: cta.
					    No exclusive-products carousel on OTC, so the CTA is always
					    sticky on desktop. */}
					<div className="[grid-area:cta] flex flex-col gap-2">
						<ProductDetailsCTA
							product={product}
							showExpertReview={false}
							expertReview={undefined}
							onExpertReviewClick={() => {}}
							ratingBreakdown={ratingBreakdown}
							localVendorId={localVendorId}
							onLocalVendorChange={handleVendorChange}
							variants={variants}
							effectivePreferredVendorId={effectivePreferredVendorId}
							isDesktopSticky={true}
						/>
					</div>

					{/* Description only (no cannabis attribute box) — area: description */}
					<Suspense
						fallback={
							<div className="[grid-area:description] space-y-2">
								<div className="h-32 animate-pulse bg-gray-100 rounded-lg" />
							</div>
						}
					>
						<div className="flex flex-col gap-2 [grid-area:description]">
							<Accordion.Root
								className="w-full flex flex-col"
								type="multiple"
								defaultValue={["description"]}
							>
								<Accordion.Item value="description" className="px-6 pt-6">
									<ProductDetailsDescriptionAccordion
										product={product}
										showAttributes={false}
									/>
								</Accordion.Item>
							</Accordion.Root>
						</div>
					</Suspense>
				</div>
			</div>
		</div>
	);
}
